import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';

// Pages
import SplashScreen from './pages/SplashScreen';
import BackgroundsPreview from './pages/BackgroundsPreview';
import BuyerAuth from './pages/BuyerAuth';
import SellerAuth from './pages/SellerAuth';
import RoleSelection from './pages/RoleSelection';
import AuthCallback from './pages/AuthCallback';
import BuyerHome from './pages/BuyerHome';
import SearchPage from './pages/SearchPage';
import ListingDetail from './pages/ListingDetail';
import MyBookings from './pages/MyBookings';
import BookingPaymentPicker from './pages/BookingPaymentPicker';
import ProfilePage from './pages/ProfilePage';
import SellerDashboard from './pages/SellerDashboard';
import SellerSlotManager from './pages/SellerSlotManager';
import AddListing from './pages/AddListing';
import SpaceManager from './pages/SpaceManager';
import AvailabilityManager from './pages/AvailabilityManager';
import ServicesManager from './pages/ServicesManager';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import AdminPanel from './pages/AdminPanel';
import ForgotPassword from './pages/ForgotPassword';
import ReviewPage from './pages/ReviewPage';
import HelplineWidget from './components/HelplineWidget';
import ChatPage from './pages/ChatPage';
import SellerAds from './pages/SellerAds';
import FAQPage from './pages/FAQPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AdminLogin from './pages/AdminLogin';
import LiveClasses from './pages/LiveClasses';
import VideoRoom from './pages/VideoRoom';

import './index.css';
import './styles/themes.css';
import useThemeBackground from './hooks/useThemeBackground';

// App Router Component - handles session_id detection
function AppRouter() {
  const location = useLocation();
  useThemeBackground();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const hideWidget = location.pathname.startsWith('/admin');

  return (
    <>
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<SplashScreen />} />
      <Route path="/preview/backgrounds" element={<BackgroundsPreview />} />
      
      {/* Separate Buyer & Seller Auth */}
      <Route path="/buyer/login" element={<BuyerAuth />} />
      <Route path="/seller/login" element={<SellerAuth />} />
      
      {/* Legacy routes - redirect to new auth */}
      <Route path="/login" element={<BuyerAuth />} />
      <Route path="/register" element={<BuyerAuth />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/live-classes" element={<LiveClasses />} />
      <Route path="/live-classes/:classId/join" element={
        <ProtectedRoute>
          <VideoRoom />
        </ProtectedRoute>
      } />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      
      {/* Role Selection */}
      <Route path="/role-selection" element={
        <ProtectedRoute>
          <RoleSelection />
        </ProtectedRoute>
      } />

      {/* Buyer Routes - Most accessible without auth for browsing */}
      <Route path="/buyer" element={<BuyerHome />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/listing/:id" element={<ListingDetail />} />
      
      {/* Protected Buyer Routes */}
      <Route path="/bookings" element={
        <ProtectedRoute>
          <MyBookings />
        </ProtectedRoute>
      } />
      <Route path="/bookings/:bookingId/pay" element={
        <ProtectedRoute>
          <BookingPaymentPicker />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/wishlist" element={
        <ProtectedRoute>
          <BuyerHome />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />

      {/* Seller Routes */}
      <Route path="/bookings/:bookingId/review" element={
        <ProtectedRoute>
          <ReviewPage />
        </ProtectedRoute>
      } />

      {/* Seller Routes */}
      <Route path="/seller" element={
        <ProtectedRoute requireSeller>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/seller/add-listing" element={
        <ProtectedRoute requireSeller>
          <AddListing />
        </ProtectedRoute>
      } />
      <Route path="/seller/listings" element={
        <ProtectedRoute requireSeller>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/seller/bookings" element={
        <ProtectedRoute requireSeller>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/seller/calendar" element={
        <ProtectedRoute requireSeller>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/seller/slots" element={
        <ProtectedRoute requireSeller>
          <SellerSlotManager />
        </ProtectedRoute>
      } />
      <Route path="/seller/earnings" element={
        <ProtectedRoute requireSeller>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/seller/edit-listing/:id" element={
        <ProtectedRoute requireSeller>
          <AddListing />
        </ProtectedRoute>
      } />
      <Route path="/seller/spaces/:listingId" element={
        <ProtectedRoute requireSeller>
          <SpaceManager />
        </ProtectedRoute>
      } />
      <Route path="/seller/availability/:listingId" element={
        <ProtectedRoute requireSeller>
          <AvailabilityManager />
        </ProtectedRoute>
      } />
      <Route path="/seller/services/:listingId" element={
        <ProtectedRoute requireSeller>
          <ServicesManager />
        </ProtectedRoute>
      } />
      <Route path="/seller/ads" element={
        <ProtectedRoute requireSeller>
          <SellerAds />
        </ProtectedRoute>
      } />
      <Route path="/seller/ads/success" element={
        <ProtectedRoute requireSeller>
          <SellerAds />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <AdminPanel />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<SplashScreen />} />
    </Routes>
    {!hideWidget && <HelplineWidget />}
    </>
  );
}

function App() {
  React.useEffect(() => {
    // Track a unique visit (deduped server-side per browser per day). Fire-and-forget.
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {}, { withCredentials: true })
      .catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
